import React, {useRef, useState} from 'react';
import {FormGroup, Input, Label} from 'reactstrap';
import {Form} from '@unform/web';
import {Input as UInput} from '../../components/input';
import {TextArea} from '../../components/textarea';
import * as Yup from 'yup';
import * as moment from 'moment';
import ReactSelect from '../../components/react-select';

import './user.sass';
import ToggleButton from '../../components/toggle-button';
import {generateToken} from '../../helpers/auth';
import I18nMessage from '../../components/I18nMessage';
import DatePicker from '../../components/react-datepicker';
import ReactInputCpfCnpj from '../../components/react-mask-cpf-cnpj';
import ReactMaskZipcode from '../../components/react-mask-zipcode';
import {FieldSet} from '../../components/fieldset';
import {GroupButtonForm} from '../../components/group-button-form';
import ReactMaskPhone from '../../components/react-mask-phone';
import ReactSelectCustomer from '../../components/react-select-customer';

const UserForm = (props) => {
    const {
        cancel,
        data,
        edit,
        newRegister,
        updateRegister,
        coordinateFormats,
        profile,
        loading,
        customer,
        states,
        azulPayErrors,
        session,
        fullControlStore,
    } = props;

    const formRef = useRef(null);
    const provider = session.attributes.payment_provider || 'AZUL_PAY';
    const apiKeySeller = !(session.attributes && session.attributes.hasOwnProperty('apiKeySeller') || session.attributes.hasOwnProperty('payment_provider'));
    const [type, setType] = useState(customer.type === 'PJ' ? 'cnpj' : 'cpf');
    const [azulPayLinkType, setAzulPayLinkType] = useState(data.attributes && data.attributes.customerType ? data.attributes.customerType : 'NONE');
    const [customerId, setCustomerId] = useState(data.attributes && data.attributes.customerId ? data.attributes.customerId : null);

    const schema = Yup.object().shape({
        id: Yup.number().notRequired(),
        name: Yup.string().required(),
        email: Yup.string().required(),
        password: Yup.string().nullable().notRequired(),
        phone: Yup.string().notRequired(),
        map: Yup.string().nullable().notRequired(),
        latitude: Yup.number().notRequired(),
        longitude: Yup.number().notRequired(),
        zoom: Yup.number().notRequired(),
        twelveHourFormat: Yup.boolean().notRequired(),
        coordinateFormat: Yup.string().nullable().notRequired(),
        poiLayer: Yup.string().nullable().notRequired(),
        disabled: Yup.boolean().notRequired(),
        administrator: Yup.boolean().notRequired(),
        readonly: Yup.boolean().notRequired(),
        deviceReadonly: Yup.boolean().notRequired(),
        limitCommands: Yup.boolean().notRequired(),
        expirationTime: Yup.string().notRequired().nullable(), // "2019-10-31T03:00:00.000+0000"
        deviceLimit: Yup.number().notRequired(),
        userLimit: Yup.number().notRequired(),
        token: Yup.string().nullable().notRequired(),
        azulPayLinkType: Yup.string().nullable().notRequired().default('NONE').strip(),
        customerDependentId: Yup.string().notRequired().strip(),
        customer: Yup.object().nullable().shape({
            id: Yup.string().notRequired(),
            cc_email: Yup.string().notRequired(),
            email: Yup.string().notRequired(),
            full_name: Yup.string().notRequired(),
            cpf_cnpj: Yup.string().notRequired(),
            postal_code: Yup.string().notRequired(),
            number: Yup.string().notRequired(),
            neighborhood: Yup.string().notRequired(),
            complement: Yup.string().notRequired(),
            street: Yup.string().notRequired(),
            city: Yup.string().notRequired(),
            state: Yup.string().nullable().notRequired(),
            type: Yup.string().nullable().notRequired(),
            notes: Yup.string().notRequired(),
        }),
    });

    const handleSubmit = async (userData) => {
        let user = Object.assign(data, userData);

        try {
            if (user.expirationTime) {
                user.expirationTime = moment(user.expirationTime).toISOString();
            }

            const attributes = data.attributes || {};

            if (customerId) {
                attributes.customerId = customerId;
            }

            attributes.customerType = azulPayLinkType;

            user.attributes = attributes;

            user = schema.cast(user);

            await schema.validate(user, {
                strict: true,
                abortEarly: false,
                stripUnknown: true,
            });

            delete user.azulPayLinkType;
            delete user.customerDependentId;

            edit ? updateRegister(user) : newRegister(user);
        } catch (err) {
            console.log(err);
            const validationErrors = {};
            if (err instanceof Yup.ValidationError) {
                err.inner.forEach((error) => {
                    console.log(error.path);
                    validationErrors[error.path] = error.message;
                });
                formRef.current.setErrors(validationErrors);
            }
        }
    };

    const cast = (src) => {
        const data = Object.assign({}, src);

        if (!data.userLimit) {
            data.userLimit = 0;
        }
        if (!data.zoom) {
            data.zoom = 0;
        }
        if (!data.expirationTime) {
            data.expirationTime = undefined;
        }
        if (!data.deviceLimit) {
            data.deviceLimit = 0;
        }
        if (!data.latitude) {
            data.latitude = 0;
        }
        if (!data.longitude) {
            data.longitude = 0;
        }
        if (data.attributes) {
            if (data.attributes.hasOwnProperty('customerId')) {
                data.customerDependentId = data.attributes.customerId;
            }
            if (data.attributes.hasOwnProperty('customerType')) {
                data.azulPayLinkType = data.attributes.customerType;
            } else {
                data.azulPayLinkType = 'NONE';
            }
        }
        data.customer = customer;

        return data;
    };

    const customerTypeOnChange = (data) => {
        setType(data.id === 'PF' ? 'cpf' : 'cnpj');
    };

    const customerOnChange = (data) => {
        const customer = fullControlStore.getClientById(data.id);

        if (customer) {
            formRef.current.setData({
                name: customer.nome,
                email: customer.contatoPrincipal.email,
                phone: customer.contatoPrincipal.celular.phone,
                azulPayLinkType: azulPayLinkType,
                customerDependentId: customer.id,
                customer: {
                    id: customer.id,
                    email: customer.contatoPrincipal.email,
                    full_name: customer.nome,
                    cpf_cnpj: customer.cnpJ_CPF,
                    postal_code: customer.enderecoPrincipal.cep,
                    number: customer.enderecoPrincipal.numero,
                    neighborhood: customer.enderecoPrincipal.bairro,
                    complement: customer.enderecoPrincipal.complemento,
                    street: customer.enderecoPrincipal.endereco,
                    city: customer.enderecoPrincipal.cidade,
                    state: customer.enderecoPrincipal.uf,
                    type: customer.getTipoPessoa(),
                    notes: '',
                },
            });
        }

        setCustomerId(data.id);
    };

    const dependentTypeOnChange = (data) => {
        setAzulPayLinkType(data.id);
    };

    const setToken = () => {
        formRef.current.setFieldValue('token', generateToken());
    };

    return (
        <>
            <Form
                ref={formRef}
                onSubmit={handleSubmit}
                schema={schema}
                initialData={cast(data)}>

                {!!edit && <UInput hidden type="number" name="id"/>}
                {!!edit && <UInput hidden type="number" name="customer.id"/>}

                <FieldSet className="text-left border p-4" title={'Identificação'}>

                    <div className="row">
                        <div className={'col'}>
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="sharedName"/>*</Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="name"
                                    id="name"
                                />
                            </FormGroup>
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="userEmail"/>*</Label>
                                <Input
                                    tag={UInput}
                                    type="email"
                                    name="email"
                                    id="email"
                                />
                            </FormGroup>
                            {azulPayErrors && azulPayErrors['email'] && azulPayErrors['email'].map((item, i) =>
                                <span key={i}>{item}</span>)}
                        </div>

                        <div className={'col'}>
                            <FormGroup className="custom-formGroup">
                                <Label for="phone"><I18nMessage id="sharedPhone"/></Label>
                                <ReactMaskPhone
                                    tag={UInput}
                                    type="number"
                                    name="phone"
                                    id="phone"
                                />
                                {azulPayErrors && azulPayErrors['phone_number'] && azulPayErrors['phone_number'].map((item, i) =>
                                    <span key={i}>{item}</span>)}
                            </FormGroup>
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="userPassword"/>*</Label>
                                <Input
                                    tag={UInput}
                                    type="password"
                                    name="password"
                                    id="password"
                                    autoComplete={'new-password'}
                                />
                            </FormGroup>
                        </div>
                    </div>
                </FieldSet>
                <br/>
                <FieldSet className="text-left border p-4" title={'Dados de cobrança'}>
                    <>
                        <div className="row">
                            <div className={'col'}>
                                <FormGroup className="custom-formGroup">
                                    <Label for="azulPayLinkType">Vincular como:</Label>
                                    <ReactSelect id={'azulPayLinkType'}
                                                 isDisabled={apiKeySeller}
                                                 onChange={dependentTypeOnChange}
                                                 name="azulPayLinkType" options={[
                                        {id: 'TT', title: 'Titular'},
                                        {id: 'DT', title: 'Dependente'},
                                    ]}/>
                                </FormGroup>

                                <FormGroup className="custom-formGroup">
                                    <Label>Tipo:</Label>
                                    <ReactSelect id={'customer.type'}
                                                 isDisabled={apiKeySeller}
                                                 onChange={customerTypeOnChange}
                                                 name="customer.type" options={[
                                        {id: 'PF', title: 'Pessoa Física'},
                                        {id: 'PJ', title: 'Pessoa Jurídica'},
                                    ]}/>
                                    {azulPayErrors && azulPayErrors['type'] && azulPayErrors['type'].map((item, i) =>
                                        <span
                                            key={i}>{item}</span>)}
                                </FormGroup>

                                <FormGroup className="custom-formGroup">
                                    <Label>Endereço</Label>
                                    <Input
                                        disabled={apiKeySeller}
                                        tag={UInput}
                                        type="string"
                                        name="customer.street"
                                        id="customer.street"
                                    />
                                    {azulPayErrors && azulPayErrors['street'] && azulPayErrors['street'].map((item, i) =>
                                        <span key={i}>{item}</span>)}
                                </FormGroup>

                                <FormGroup className="custom-formGroup">
                                    <Label>Complemento</Label>
                                    <Input
                                        disabled={apiKeySeller}
                                        tag={UInput}
                                        type="string"
                                        name="customer.complement"
                                        id="customer.complement"
                                    />
                                    {azulPayErrors && azulPayErrors['complement'] && azulPayErrors['complement'].map((item, i) =>
                                        <span key={i}>{item}</span>)}
                                </FormGroup>

                                <FormGroup className="custom-formGroup">
                                    <Label>Cidade</Label>
                                    <Input
                                        disabled={apiKeySeller}
                                        tag={UInput}
                                        type="string"
                                        name="customer.city"
                                        id="customer.city"
                                    />
                                    {azulPayErrors && azulPayErrors['city'] && azulPayErrors['city'].map((item, i) =>
                                        <span
                                            key={i}>{item}</span>)}
                                </FormGroup>
                            </div>
                            <div className={'col'}>

                                {azulPayLinkType !== 'NONE' && <FormGroup className="custom-formGroup">
                                    <Label for="customerId">Cliente:</Label>
                                    <ReactSelectCustomer
                                        id={'customerDependentId'}
                                        disabled={apiKeySeller}
                                        onChange={customerOnChange}
                                        name="customerDependentId"
                                        provider={provider}
                                    />
                                </FormGroup>}

                                <FormGroup className="custom-formGroup">
                                    <Label>CPF/CNPJ</Label>
                                    <ReactInputCpfCnpj
                                        disabled={apiKeySeller}
                                        name="customer.cpf_cnpj"
                                        id="customer.cpf_cnpj"
                                        type={type}
                                    />
                                    {azulPayErrors && azulPayErrors['cpf_cnpj'] && azulPayErrors['cpf_cnpj'].map((item, i) =>
                                        <span key={i}>{item}</span>)}
                                </FormGroup>

                                <FormGroup className="custom-formGroup">
                                    <Label>CEP</Label>
                                    <ReactMaskZipcode
                                        disabled={apiKeySeller}
                                        name="customer.postal_code"
                                        id="customer.postal_code"
                                    />
                                    {azulPayErrors && azulPayErrors['postal_code'] && azulPayErrors['postal_code'].map((item, i) =>
                                        <span key={i}>{item}</span>)}
                                </FormGroup>

                                <FormGroup className="custom-formGroup">
                                    <Label>Número</Label>
                                    <Input
                                        tag={UInput}
                                        disabled={apiKeySeller}
                                        type="string"
                                        name="customer.number"
                                        id="customer.number"
                                    />
                                    {azulPayErrors && azulPayErrors['number'] && azulPayErrors['number'].map((item, i) =>
                                        <span key={i}>{item}</span>)}
                                </FormGroup>

                                <FormGroup className="custom-formGroup">
                                    <Label>Bairro</Label>
                                    <Input
                                        tag={UInput}
                                        disabled={apiKeySeller}
                                        type="string"
                                        name="customer.neighborhood"
                                        id="customer.neighborhood"
                                    />
                                    {azulPayErrors && azulPayErrors['neighborhood'] && azulPayErrors['neighborhood'].map((item, i) =>
                                        <span key={i}>{item}</span>)}

                                </FormGroup>

                                <FormGroup className="custom-formGroup">
                                    <Label>Estado</Label>
                                    <ReactSelect
                                        isDisabled={apiKeySeller}
                                        id={'customer.state'}
                                        disabled={apiKeySeller}
                                        name="customer.state"
                                        options={states}
                                    />
                                    {azulPayErrors && azulPayErrors['state'] && azulPayErrors['state'].map((item, i) =>
                                        <span key={i}>{item}</span>)}

                                </FormGroup>

                            </div>

                        </div>

                        <div className="row">
                            <div className={'col'}>
                                <FormGroup className="custom-formGroup">
                                    <Label>Notas</Label>
                                    <TextArea
                                        disabled={apiKeySeller}
                                        type="string"
                                        name="customer.notes"
                                        id="customer.notes"
                                    />
                                </FormGroup>
                            </div>
                        </div>
                    </>
                </FieldSet>
                <br/>
                <FieldSet className="text-left border p-4" title={'Permissões'}>
                    <div className="row">
                        <div className={'col'}>
                            <FormGroup className="custom-formGroup">
                                <Label><I18nMessage id="sharedDisabled"/></Label>
                                <ToggleButton name={'disabled'} id={'disabled'}/>
                            </FormGroup>
                            {profile === 'admin' &&
                            <FormGroup className="custom-formGroup">
                                <Label><I18nMessage id="userAdmin"/></Label>
                                <ToggleButton name={'administrator'} id={'administrator'}/>
                            </FormGroup>
                            }
                            <FormGroup className="custom-formGroup">
                                <Label><I18nMessage id="serverReadonly"/></Label>
                                <ToggleButton name={'readonly'} id={'readonly'}/>
                            </FormGroup>
                            <FormGroup className="custom-formGroup">
                                <Label><I18nMessage id="userDeviceReadonly"/></Label>
                                <ToggleButton name={'deviceReadonly'} id={'deviceReadonly'}/>
                            </FormGroup>
                        </div>
                        <div className={'col'}>
                            <FormGroup className="custom-formGroup">
                                <Label><I18nMessage id="userLimitCommands"/></Label>
                                <ToggleButton name={'limitCommands'} id={'limitCommands'}/>
                            </FormGroup>

                            <FormGroup className="custom-formGroup">
                                <Label><I18nMessage id="settingsTwelveHourFormat"/></Label>
                                <ToggleButton name={'twelveHourFormat'} id={'twelveHourFormat'}/>
                            </FormGroup>
                        </div>
                    </div>
                </FieldSet>
                <br/>
                <FieldSet className="text-left border p-4" title={'Configurações'}>
                    <div className="row">
                        <div className={'col'}>
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="positionLatitude"/></Label>
                                <Input
                                    tag={UInput}
                                    type="number"
                                    name="latitude"
                                    id="latitude"
                                />
                            </FormGroup>
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="positionLongitude"/></Label>
                                <Input
                                    tag={UInput}
                                    type="number"
                                    name="longitude"
                                    id="longitude"
                                />
                            </FormGroup>
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="serverZoom"/></Label>
                                <Input
                                    tag={UInput}
                                    type="number"
                                    name="zoom"
                                    id="zoom"
                                />
                            </FormGroup>
                        </div>
                        <div className={'col'}>
                            <FormGroup className="custom-formGroup">
                                <Label><I18nMessage id="settingsCoordinateFormat"/></Label>
                                <ReactSelect
                                    id={'coordinateFormat'}
                                    name="coordinateFormat"
                                    options={coordinateFormats}/>
                            </FormGroup>
                            {profile === 'admin' &&
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="userDeviceLimit"/></Label>
                                <Input
                                    tag={UInput}
                                    type="number"
                                    name="deviceLimit"
                                    id="deviceLimit"
                                />
                            </FormGroup>
                            }
                            {profile === 'admin' &&
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="userUserLimit"/></Label>
                                <Input
                                    tag={UInput}
                                    type="number"
                                    name="userLimit"
                                    id="userLimit"
                                />
                            </FormGroup>
                            }
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="userExpirationTime"/></Label>
                                <DatePicker
                                    name={'expirationTime'}
                                    displayFormat={'dd/MM/yyyy'}
                                />
                            </FormGroup>
                            <FormGroup className="custom-formGroup">
                                <Label for="token"><I18nMessage id="userToken"/></Label>
                                <div className="input-group mb-2">
                                    <Input
                                        tag={UInput}
                                        type="text"
                                        name="token"
                                        id="token"
                                    />
                                        <div className="input-group-prepend">
                                            <div className="input-group-text token">
                                                <span color="primary" onClick={() => setToken()}>
                                                    <i className="fas fa-sync-alt"/>
                                                </span>
                                            </div>
                                        </div>
                                </div>
                            </FormGroup>
                        </div>
                    </div>
                </FieldSet>
                <br/>
                <GroupButtonForm
                    loading={loading}
                    cancel={cancel}
                    submit={() => formRef.current.submitForm()}
                />
            </Form>
        </>
    );
};

export default UserForm;
